import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Layout, Typography, Card, List, Spin, Alert, Tabs, Statistic, Button, Tag, Space, Segmented, Row, Col, App, Table, Tooltip,
} from 'antd';
import {
  WhatsAppOutlined, ReloadOutlined, CheckCircleFilled, EditOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAReceber } from '../../services/clienteService';
import { updateTransacao } from '../../services/transactionService';
import { getContas } from '../../services/accountService';
import { getCategorias } from '../../services/categoryService';
import { getErrorMessage } from '../../utils/errorHandler';
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';
import CobrancaModal from '../../components/CobrancaModal/CobrancaModal';
import TransacaoModal from '../../components/TransacaoModal/TransacaoModal';
import EmptyStateGuide from '../../components/EmptyStateGuide/EmptyStateGuide';

const { Content } = Layout;
const { Title, Text } = Typography;

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

const formatDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};

function diasRelativo(dataVencimento) {
  if (!dataVencimento) return null;
  const hoje = dayjs().startOf('day');
  const venc = dayjs(String(dataVencimento).slice(0, 10)).startOf('day');
  return venc.diff(hoje, 'day');
}

function labelDias(dias) {
  if (dias == null) return null;
  if (dias < 0) return `${Math.abs(dias)} dia${Math.abs(dias) === 1 ? '' : 's'} em atraso`;
  if (dias === 0) return 'Vence hoje';
  return `Vence em ${dias} dia${dias === 1 ? '' : 's'}`;
}

function AReceberPage() {
  const { message, modal } = App.useApp();
  const { user } = useAuth();
  const { isPrivate } = usePrivacy();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [soCobrancas, setSoCobrancas] = useState(false);
  const [data, setData] = useState({
    total_aberto: 0,
    total_vencidas: 0,
    total_a_vencer: 0,
    total_recebidas_mes: 0,
    qtd_vencidas: 0,
    qtd_a_vencer: 0,
    qtd_recebidas_mes: 0,
    vencidas: [],
    a_vencer: [],
    recebidas_mes: [],
  });
  const [cobrancaTx, setCobrancaTx] = useState(null);
  const [editTx, setEditTx] = useState(null);
  const [contas, setContas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    Promise.all([getContas(), getCategorias()])
      .then(([c, cats]) => {
        setContas(Array.isArray(c) ? c : c?.results || []);
        setCategorias(Array.isArray(cats) ? cats : cats?.results || []);
      })
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAReceber(soCobrancas ? { so_cobrancas: true } : {});
      setData({
        total_aberto: res.total_aberto || 0,
        total_vencidas: res.total_vencidas || 0,
        total_a_vencer: res.total_a_vencer || 0,
        total_recebidas_mes: res.total_recebidas_mes || 0,
        qtd_vencidas: res.qtd_vencidas ?? (res.vencidas || []).length,
        qtd_a_vencer: res.qtd_a_vencer ?? (res.a_vencer || []).length,
        qtd_recebidas_mes: res.qtd_recebidas_mes ?? (res.recebidas_mes || []).length,
        vencidas: res.vencidas || [],
        a_vencer: res.a_vencer || [],
        recebidas_mes: res.recebidas_mes || [],
      });
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Falha ao carregar A receber.'));
    } finally {
      setLoading(false);
    }
  }, [soCobrancas]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleMarcarRecebido = (t) => {
    modal.confirm({
      title: 'Marcar como recebido?',
      content: `"${t.descricao}" será marcada como paga hoje.`,
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await updateTransacao(t.id, {
            status: 'PAGO',
            data_pagamento: dayjs().format('YYYY-MM-DD'),
          });
          message.success('Receita marcada como recebida.');
          fetchData();
        } catch (err) {
          message.error(getErrorMessage(err, 'Não foi possível atualizar.'));
        }
      },
    });
  };

  const renderActions = (t, { showCobrar } = {}) => (
    <Space size={0} wrap={false}>
      {showCobrar && (
        isMobile ? (
          <Button
            size="small"
            icon={<CheckCircleFilled />}
            type="text"
            style={{ color: 'var(--ant-color-success)' }}
            onClick={() => handleMarcarRecebido(t)}
            disabled={loading}
          >
            Recebido
          </Button>
        ) : (
          <Tooltip title="Recebido">
            <Button
              icon={<CheckCircleFilled />}
              type="text"
              style={{ color: 'var(--ant-color-success)' }}
              onClick={() => handleMarcarRecebido(t)}
              disabled={loading}
            />
          </Tooltip>
        )
      )}
      {isMobile ? (
        <Button size="small" icon={<EditOutlined />} type="text" onClick={() => setEditTx(t)} disabled={loading}>
          Abrir
        </Button>
      ) : (
        <Tooltip title="Abrir">
          <Button icon={<EditOutlined />} type="text" onClick={() => setEditTx(t)} disabled={loading} />
        </Tooltip>
      )}
      {showCobrar && t.eh_cobranca ? (
        isMobile ? (
          <Button
            size="small"
            icon={<WhatsAppOutlined />}
            type="text"
            style={{ color: '#25D366' }}
            onClick={() => setCobrancaTx(t)}
            disabled={loading}
          >
            Cobrar
          </Button>
        ) : (
          <Tooltip title="Cobrar">
            <Button
              icon={<WhatsAppOutlined />}
              type="text"
              style={{ color: '#25D366' }}
              onClick={() => setCobrancaTx(t)}
              disabled={loading}
            />
          </Tooltip>
        )
      ) : null}
    </Space>
  );

  const renderListItem = (t, { showCobrar } = {}) => {
    const nome = t.cliente_nome || t.nome_devedor || '—';
    const dias = showCobrar ? diasRelativo(t.data_vencimento) : null;
    const diasLabel = labelDias(dias);
    return (
      <List.Item>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Space wrap size={4}>
                <Text strong>{t.descricao}</Text>
                {t.eh_cobranca && <Tag color="gold">Cobrança</Tag>}
              </Space>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {`${nome} · Vence ${formatDate(t.data_vencimento)}`}
                </Text>
              </div>
              {diasLabel && (
                <Text type={dias < 0 ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                  {diasLabel}
                </Text>
              )}
            </div>
            <Text strong style={{ color: showCobrar && dias < 0 ? '#D64545' : undefined, whiteSpace: 'nowrap' }}>
              {isPrivate ? '••••' : formatCurrency(t.valor)}
            </Text>
          </div>
          {renderActions(t, { showCobrar })}
        </div>
      </List.Item>
    );
  };

  const tableColumns = useCallback((showCobrar) => [
    {
      title: 'Descrição',
      dataIndex: 'descricao',
      key: 'descricao',
      ellipsis: true,
      render: (text, record) => (
        <Space wrap size={4}>
          <span>{text}</span>
          {record.eh_cobranca && <Tag color="gold">Cobrança</Tag>}
        </Space>
      ),
    },
    {
      title: 'Cliente',
      key: 'cliente',
      width: 160,
      ellipsis: true,
      render: (_, r) => r.cliente_nome || r.nome_devedor || '—',
    },
    {
      title: 'Vencimento',
      dataIndex: 'data_vencimento',
      key: 'data_vencimento',
      width: 120,
      render: (d) => formatDate(d),
    },
    ...(showCobrar
      ? [{
          title: 'Dias',
          key: 'dias',
          width: 150,
          render: (_, r) => {
            const dias = diasRelativo(r.data_vencimento);
            const label = labelDias(dias);
            if (!label) return '—';
            return (
              <Text type={dias < 0 ? 'danger' : 'secondary'} style={{ fontSize: 13 }}>
                {label}
              </Text>
            );
          },
        }]
      : []),
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      width: 130,
      align: 'right',
      render: (v, r) => {
        const dias = showCobrar ? diasRelativo(r.data_vencimento) : null;
        return (
          <Text strong style={{ color: showCobrar && dias < 0 ? '#D64545' : undefined }}>
            {isPrivate ? '••••' : formatCurrency(v)}
          </Text>
        );
      },
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: showCobrar ? 120 : 56,
      align: 'right',
      render: (_, r) => renderActions(r, { showCobrar }),
    },
  ], [isPrivate, isMobile, loading]);

  const emptyVencidas = useMemo(
    () => (
      <EmptyStateGuide
        title={soCobrancas ? 'Nenhuma cobrança vencida.' : 'Nenhuma receita vencida.'}
        description="Quando houver pendências em atraso, elas aparecem aqui."
      />
    ),
    [soCobrancas],
  );

  const renderTabContent = (items, emptyText, showCobrar) => {
    if (isMobile) {
      return (
        <List
          loading={loading}
          dataSource={items}
          locale={{ emptyText }}
          renderItem={(t) => renderListItem(t, { showCobrar })}
        />
      );
    }
    return (
      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        columns={tableColumns(showCobrar)}
        pagination={items.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
        locale={{ emptyText }}
        size="middle"
        scroll={{ x: showCobrar ? 780 : 640 }}
      />
    );
  };

  if (loading && !data.vencidas.length && !data.a_vencer.length) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <div className="brand-skeleton" style={{ padding: 24 }} aria-busy="true" aria-label="Carregando">
  <div className="brand-skeleton-line brand-skeleton-line--lg" />
  <div className="brand-skeleton-line brand-skeleton-line--md" />
  <div className="brand-skeleton-line" />
  <div className="brand-skeleton-line brand-skeleton-line--sm" />
</div>
      </div>
    );
  }

  return (
    <Content style={{ padding: isMobile ? '16px' : '24px' }}>
      <Card style={{ marginBottom: 24 }} bodyStyle={{ padding: isMobile ? '16px' : '24px' }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} md={10}>
            <Title level={2} style={{ margin: 0 }}>A receber</Title>
            <Text type="secondary">Receitas em conta pendentes e recebidas no mês.</Text>
          </Col>
          <Col xs={24} md={14} style={{ display: 'flex', gap: 12, justifyContent: isMobile ? 'flex-start' : 'flex-end', flexWrap: 'wrap' }}>
            <Segmented
              value={soCobrancas ? 'cobrancas' : 'todas'}
              onChange={(v) => setSoCobrancas(v === 'cobrancas')}
              block={isMobile}
              options={[
                { label: 'Todas as receitas', value: 'todas' },
                { label: 'Só cobranças', value: 'cobrancas' },
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={fetchData} block={isMobile}>
              Atualizar
            </Button>
          </Col>
        </Row>
      </Card>

      {error && <Alert message="Erro" description={error} type="error" showIcon style={{ marginBottom: 16 }} />}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
            <Statistic
              title="Total em aberto"
              value={isPrivate ? undefined : Number(data.total_aberto)}
              formatter={isPrivate ? () => '••••' : (v) => formatCurrency(v)}
              valueStyle={{ color: '#D64545', fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
            <Statistic
              title={`Vencidas (${data.qtd_vencidas})`}
              value={isPrivate ? undefined : Number(data.total_vencidas)}
              formatter={isPrivate ? () => '••••' : (v) => formatCurrency(v)}
              valueStyle={{ fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
            <Statistic
              title={`A vencer (${data.qtd_a_vencer})`}
              value={isPrivate ? undefined : Number(data.total_a_vencer)}
              formatter={isPrivate ? () => '••••' : (v) => formatCurrency(v)}
              valueStyle={{ fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" bodyStyle={{ padding: isMobile ? 12 : 16 }}>
            <Statistic
              title={`Recebidas no mês (${data.qtd_recebidas_mes})`}
              value={isPrivate ? undefined : Number(data.total_recebidas_mes)}
              formatter={isPrivate ? () => '••••' : (v) => formatCurrency(v)}
              valueStyle={{ color: '#2F9E6F', fontSize: isMobile ? 16 : 20 }}
            />
          </Card>
        </Col>
      </Row>

      <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
        <Tabs
          type="card"
          className="sa-page-tabs"
          items={[
            {
              key: 'vencidas',
              label: `Vencidas (${data.qtd_vencidas})`,
              children: renderTabContent(data.vencidas, emptyVencidas, true),
            },
            {
              key: 'a_vencer',
              label: `A vencer (${data.qtd_a_vencer})`,
              children: renderTabContent(
                data.a_vencer,
                soCobrancas ? 'Nenhuma cobrança a vencer.' : 'Nenhuma receita a vencer.',
                true,
              ),
            },
            {
              key: 'recebidas',
              label: `Recebidas no mês (${data.qtd_recebidas_mes})`,
              children: renderTabContent(data.recebidas_mes, 'Nenhuma receita recebida neste mês.', false),
            },
          ]}
        />
      </Card>

      <CobrancaModal
        visible={!!cobrancaTx}
        onClose={() => {
          setCobrancaTx(null);
          fetchData();
        }}
        transacao={cobrancaTx}
        userPix={user?.chave_pix_padrao}
        userBanco={user?.banco_preferencia}
      />

      <TransacaoModal
        isOpen={!!editTx}
        onClose={() => setEditTx(null)}
        onSave={(ok) => {
          if (ok) {
            setEditTx(null);
            fetchData();
          }
        }}
        transacaoToEdit={editTx}
        contas={contas}
        allCategorias={categorias}
        isMobile={isMobile}
      />
    </Content>
  );
}

export default AReceberPage;
